import {
  AirplaneTakeoffIcon,
  AirplaneLandingIcon,
} from "@phosphor-icons/react";
import { useState } from "react";
import { useWatch } from "react-hook-form";
import { useTranslations } from "use-intl";

import { DrawerTopBar } from "@/components/Drawer/DrawerTopBar";
import { SwipeableDrawer } from "@/components/Drawer/SwipeableDrawer";
import { AirportType } from "@/features/flights/types/types";
import { LocationPicker } from "@/features/search/components/LocationPicker";
import {
  type SearchSource,
  rudderAnalytics,
  FlightSearchTrackingEvents,
} from "@/lib/rudderstack";

import { AirportSearch } from "../components/AirportSearch/AirportSearch";
import { SwapValuesButton } from "../components/SwapValuesButton/SwapValuesButton";
import type { FlightSearchForm } from "../FlightSearchForm";

type TripLocationPickerProps = {
  form: FlightSearchForm;
  analyticsSource: SearchSource;
  showErrorsInline?: boolean;
};

export function FlightLocationPicker({
  analyticsSource,
  form,
  showErrorsInline,
}: TripLocationPickerProps) {
  const t = useTranslations();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [targetField, setTargetField] = useState<AirportType>(AirportType.FROM);

  const [from, to] = useWatch({
    control: form.control,
    name: ["from", "to"],
  });

  const swapAirportValues = () => {
    const currentFrom = form.getValues("from");
    const currentTo = form.getValues("to");

    form.setValue("from", currentTo);
    form.setValue("to", currentFrom);
  };

  return (
    <>
      <div className="relative flex flex-col gap-[inherit]">
        <LocationPicker
          startIcon={<AirplaneTakeoffIcon />}
          label={t("flights.search.from")}
          value={from}
          onPickerOpen={() => {
            rudderAnalytics.track(
              FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_ORIGIN_INPUT,
              { source: analyticsSource },
            );

            setTargetField(AirportType.FROM);
            setIsDrawerOpen(true);
          }}
          errorMessage={
            showErrorsInline ? form.formState.errors.from?.message : undefined
          }
        />

        <SwapValuesButton
          onClick={swapAirportValues}
          className="absolute end-5 top-1/2 z-10 -translate-y-1/2"
        />

        <LocationPicker
          startIcon={<AirplaneLandingIcon />}
          label={t("flights.search.to")}
          value={to}
          onPickerOpen={() => {
            rudderAnalytics.track(
              FlightSearchTrackingEvents.USER_CLICKED_FLIGHT_DESTINATION_INPUT,
              { source: analyticsSource },
            );

            setTargetField(AirportType.TO);
            setIsDrawerOpen(true);
          }}
          errorMessage={
            showErrorsInline
              ? form.formState.errors.to?.name?.message
              : undefined
          }
        />
      </div>

      {/* // TODO: Review note on prev version */}
      <SwipeableDrawer
        hasFullHeight
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        disableRestoreFocus
        heightOffset={24}
        ModalProps={{
          keepMounted: false,
        }}
      >
        <DrawerTopBar title={t("flights.search.flightRoute")} />

        <AirportSearch
          values={{ from, to }}
          onChange={form.setValue}
          onDrawerClose={() => setIsDrawerOpen(false)}
          initialFocusOn={targetField}
          analyticsSource={analyticsSource}
          inSwipeableDrawer
        />
      </SwipeableDrawer>
    </>
  );
}
